import React from "react";
import {
  fetchMunicipalitiesWithLines,
  fetchZonesForLines,
} from "../../../../api/zones";
import {
  Alert,
  AlertTitle,
  AlertDescription,
  MultiSelect,
  Spinner,
  Label,
  Button,
  Heading,
} from "@ogt/ui";
import { selectionSummary } from "../../../../utils/selectionSummary";
import { useZones } from "../../../../hooks/useZones";
import type { MunicipalityWithLines } from "../../../../api/zones";

interface ZoneSectionProps {
  style?: React.CSSProperties;
  className?: string;
}

const ALL_ZONES_VALUE = "__all_zones__";

// Section that shows municipalities and allows selecting entire lines (which
// expands to their zones via the global zone state hooks).
export function ZoneSection({ style, className }: ZoneSectionProps) {
  const { selectedZones, setActiveZoneId, addAndActivateZones, removeZones } =
    useZones();

  const [municipalities, setMunicipalities] = React.useState<
    MunicipalityWithLines[]
  >([]);
  const [loadingMunicipalities, setLoadingMunicipalities] =
    React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [loadingLine, setLoadingLine] = React.useState<Record<number, boolean>>(
    {}
  );

  const baseId = React.useId();

  React.useEffect(() => {
    (async () => {
      setLoadingMunicipalities(true);
      setError(null);
      try {
        const data = await fetchMunicipalitiesWithLines();
        setMunicipalities(data);
      } catch (e) {
        console.error("Misslyckades hämta kommuner med linjer:", e);
        setError("Kunde inte ladda kommuner och linjer just nu.");
      } finally {
        setLoadingMunicipalities(false);
      }
    })();
  }, []);

  // fetched municipality data; fall back to the id string if not found.
  const getDisplayName = React.useCallback(
    (id: number | string) => {
      const s = String(id);
      for (const m of municipalities) {
        const hit = m.Value.find((v) => String(v.LineNrReal) === s);
        if (hit) {
          return hit.Ogt?.OgtLineNamePreformatted ?? hit.LineName ?? s;
        }
      }
      return s;
    },
    [municipalities]
  );

  // Derive which lines are currently selected from the global zone state (Jotai).
  const checkedLines = React.useMemo(() => {
    const set = new Set<number>();
    for (const z of selectedZones) {
      const [lnStr] = z.id.split("_");
      const ln = Number(lnStr);
      if (!Number.isNaN(ln)) set.add(ln);
    }
    return set;
  }, [selectedZones]);

  // Toggle handler for selecting/deselecting a whole line.
  async function handleLineToggle(
    lineNrReal: number,
    displayName: string,
    nextChecked: boolean
  ) {
    if (loadingLine[lineNrReal]) return;
    setLoadingLine((s) => ({ ...s, [lineNrReal]: true }));
    try {
      if (nextChecked) {
        const raw = await fetchZonesForLines([lineNrReal]);
        const zones = raw;
        addAndActivateZones(zones);
        if (zones.length) setActiveZoneId(zones[0].id);
      } else {
        const ids = selectedZones
          .filter((z) => z.id.startsWith(`${lineNrReal}_`))
          .map((z) => z.id);
        removeZones(ids);
      }
    } finally {
      setLoadingLine((s) => ({ ...s, [lineNrReal]: false }));
    }
  }

  function buildOptionsForMunicipality(m: MunicipalityWithLines) {
    const uniqueLines = Array.from(
      new Map(m.Value.map((line) => [line.LineNrReal, line])).values()
    );

    const allSelected =
      uniqueLines.length > 0 &&
      uniqueLines.every((line) => checkedLines.has(line.LineNrReal));

    const busy = isMunicipalityLoading(m);

    return [
      {
        value: ALL_ZONES_VALUE,
        label: allSelected ? "Dölj alla" : "Visa alla",
        disabled: busy || uniqueLines.length === 0,
        keywords: `${m.Key} alla områden`,
      },
      ...uniqueLines.map((line) => {
        const id = String(line.LineNrReal);

        return {
          value: id,
          label: line.Ogt?.OgtLineNamePreformatted ?? line.LineName ?? id,
          disabled: busy,
          keywords: `${m.Key} ${line.LineName} ${
            line.Ogt?.OgtLineNamePreformatted ?? ""
          }`,
        };
      }),
    ];
  }

  function selectedValuesForMunicipality(m: MunicipalityWithLines) {
    const ids = Array.from(
      new Set(m.Value.map((line) => String(line.LineNrReal)))
    );

    const selected = ids.filter((id) => checkedLines.has(Number(id)));

    const allSelected = ids.length > 0 && selected.length === ids.length;

    return allSelected ? [ALL_ZONES_VALUE, ...selected] : selected;
  }

  // Compare previous vs next selection and trigger toggles for differences.
  async function handleMunicipalityToggle(
    municipality: MunicipalityWithLines,
    show: boolean
  ) {
    if (isMunicipalityLoading(municipality)) return;

    const lineIds = Array.from(
      new Set(municipality.Value.map((line) => line.LineNrReal))
    );

    if (!show) {
      const lineIdSet = new Set(lineIds.map(String));

      const idsToRemove = selectedZones
        .filter((zone) => lineIdSet.has(String(zone.id).split("_")[0]))
        .map((zone) => zone.id);

      removeZones(idsToRemove);
      return;
    }

    const missingLineIds = lineIds.filter((id) => !checkedLines.has(id));

    if (!missingLineIds.length) return;

    setError(null);

    setLoadingLine((previous) => {
      const next = { ...previous };

      for (const id of missingLineIds) {
        next[id] = true;
      }

      return next;
    });

    try {
      const results = await Promise.allSettled(
        missingLineIds.map((id) => fetchZonesForLines([id]))
      );

      const zones = results.flatMap((result) =>
        result.status === "fulfilled" ? result.value : []
      );

      if (zones.length > 0) {
        addAndActivateZones(zones);
        setActiveZoneId(zones[0].id);
      }

      if (results.some((result) => result.status === "rejected")) {
        setError(
          `Alla närtrafikområden i ${municipality.Key} kunde inte hämtas. Försök igen.`
        );
      }
    } catch (error) {
      console.error("Kunde inte visa närtrafikområden:", error);

      setError(`Kunde inte visa närtrafikområden i ${municipality.Key}.`);
    } finally {
      setLoadingLine((previous) => {
        const next = { ...previous };

        for (const id of missingLineIds) {
          delete next[id];
        }

        return next;
      });
    }
  }

  function onMunicipalityChange(
    municipality: MunicipalityWithLines,
    nextValues: string[]
  ) {
    if (isMunicipalityLoading(municipality)) return;

    const previousValues = selectedValuesForMunicipality(municipality);

    const hadAll = previousValues.includes(ALL_ZONES_VALUE);
    const hasAll = nextValues.includes(ALL_ZONES_VALUE);

    if (hadAll !== hasAll) {
      void handleMunicipalityToggle(municipality, hasAll);
      return;
    }

    const allowedIds = new Set(
      municipality.Value.map((line) => String(line.LineNrReal))
    );

    const previous = new Set(previousValues.filter((id) => allowedIds.has(id)));

    const next = new Set(nextValues.filter((id) => allowedIds.has(id)));

    for (const id of allowedIds) {
      const wasSelected = previous.has(id);
      const shouldBeSelected = next.has(id);

      if (wasSelected === shouldBeSelected) continue;

      const lineId = Number(id);

      void handleLineToggle(
        lineId,
        getDisplayName(lineId),
        shouldBeSelected
      ).catch((error) => {
        console.error("Kunde inte ändra närtrafikområde:", error);
        setError("Kunde inte ändra området. Försök igen.");
      });
    }
  }

  function isMunicipalityLoading(m: MunicipalityWithLines) {
    for (let i = 0; i < m.Value.length; i++) {
      if (loadingLine[m.Value[i].LineNrReal]) return true;
    }
    return false;
  }

  function handleClearZones() {
    if (Object.values(loadingLine).some(Boolean)) return;
    const zoneIds = selectedZones.map((zone) => zone.id);

    if (zoneIds.length === 0) return;

    removeZones(zoneIds);
    setActiveZoneId(null);
  }

  const [activeMunicipalityKey, setActiveMunicipalityKey] = React.useState<
    string | null
  >(null);

  function attachActiveHandlers(key: string) {
    return {
      onFocusCapture: () => setActiveMunicipalityKey(key),
      onBlurCapture: (e: React.FocusEvent<HTMLDivElement>) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setActiveMunicipalityKey((cur) => (cur === key ? null : cur));
        }
      },
    };
  }

  const getMunicipalitySummary = React.useCallback(
    (m: MunicipalityWithLines) => {
      const isLoading = isMunicipalityLoading(m);
      const total = m.Value.length;
      const selected = m.Value.reduce(
        (acc, ln) => acc + (checkedLines.has(ln.LineNrReal) ? 1 : 0),
        0
      );

      return selectionSummary(
        isLoading,
        total,
        selected,
        "närtrafikområden",
        "närtrafikområde"
      );
    },
    [checkedLines]
  );

  if (loadingMunicipalities) {
    return (
      <div className="flex gap-4 p-4 justify-center items-center">
        <Spinner className="w-6 h-6" />
        <div className="text-md">Laddar kommuner…</div>
      </div>
    );
  }

  if (error && municipalities.length === 0) {
    return (
      <div className={className} style={style}>
        <div className="p-4 pb-6">
          <Alert
            variant="error"
            onDismiss={() => setError(null)}
            onOpenChange={() => {}}
          >
            <AlertTitle>{error}</AlertTitle>
            <AlertDescription>
              Kontrollera din uppkoppling eller försök igen senare.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className={className} style={style}>
      <div className="p-4 pb-6 pt-0">
        <Heading>Närtrafik</Heading>
        <p>
          Länet är indelat i olika närtrafikområden. I varje område finns
          särskilda närtrafikhållplatser som du kan resa till med närtrafiken. I
          rullistan för varje kommun väljer du vilka områden du vill se i
          kartan.
        </p>
        {error && municipalities.length > 0 && (
          <div className="mb-3">
            <Alert
              variant="error"
              onDismiss={() => setError(null)}
              onOpenChange={() => {}}
            >
              <AlertTitle>{error}</AlertTitle>
            </Alert>
          </div>
        )}

        <div className="flex flex-col gap-4">
          {municipalities.map((m, index) => {
            const selectId = `${baseId}-${m.Key}-select`;
            const labelId = `${baseId}-${m.Key}-label`;
            const summary = getMunicipalitySummary(m);
            const isLast = index === municipalities.length - 1;
            const municipalLoading = isMunicipalityLoading(m);
            const showSpinner =
              municipalLoading &&
              (activeMunicipalityKey ? activeMunicipalityKey === m.Key : true);

            return (
              <div key={m.Key} {...attachActiveHandlers(m.Key)}>
                <div className="flex items-center gap-2 h-6 mb-1">
                  <Label
                    id={labelId}
                    htmlFor={selectId}
                    className="truncate mb-0"
                  >
                    {m.Key}
                  </Label>
                  {showSpinner && <Spinner className="ml-auto w-6 h-6" />}
                </div>

                <MultiSelect
                  id={selectId}
                  options={buildOptionsForMunicipality(m)}
                  value={selectedValuesForMunicipality(m)}
                  onValueChange={(vals) => onMunicipalityChange(m, vals)}
                  placeholder={summary}
                  ariaLabelledBy={labelId}
                  showCounter={false}
                  side={isLast ? "top" : "bottom"}
                  className="bg-white"
                  size="default"
                />
              </div>
            );
          })}
        </div>
        <Button
          type="button"
          variant="secondary"
          showIcon
          iconPosition="left"
          iconName="trashcan"
          onClick={handleClearZones}
          disabled={
            selectedZones.length === 0 ||
            Object.values(loadingLine).some(Boolean)
          }
          className="mt-4 w-full"
        >
          Rensa alla val
        </Button>
      </div>
    </div>
  );
}
